import * as React from 'react';
import { useStore } from 'react-admin';
import { useQuery } from '@tanstack/react-query';
// import CustomOnlineStoreItem from './CustomOnlineStoreItem';

// Configuration for all store keys
const STORE_KEYS = [
    'preferences.user.datagrid.columns',
    'user.listParams',
    'user.filterList',
    'user.savedQueries',
    'preferences.teacher.datagrid.columns',
    'teacher.listParams',
    'teacher.filterList',
    'teacher.filters.schools_q',
    'teacher.filters.schools_result',
    'teacher.savedQueries',
    'preferences.student.datagrid.columns',
    'student.listParams',
    'student.filterList',
    'student.filters.schools_q',
    'student.filters.schools_result',
    'student.savedQueries',
    'preferences.pupil.datagrid.columns',
    'pupil.listParams',
    'pupil.filterList',
    'pupil.filters.schools_q',
    'pupil.filters.schools_result',
    'pupil.savedQueries',
    'preferences.product.datagrid.columns',
    'product.listParams',
    'product.filterList',
    'product.savedQueries',
    'preferences.subscription.datagrid.columns',
    'subscription.listParams',
    'subscription.filterList',
    'subscription.filters.product_quid_q',
    'subscription.filters.product_quid_result',
    'subscription.filters.subscribers_q',
    'subscription.filters.subscribers_result',
    'subscription.savedQueries',
    'preferences.school.datagrid.columns',
    'school.listParams',
    'school.filterList',
    'school.savedQueries',
    'preferences.group.datagrid.columns',
    'group.listParams',
    'group.filterList',
    'group.filters.schools_q',
    'group.filters.schools_result',
    'group.savedQueries',
    'preferences.announcement.datagrid.columns',
    'announcement.listParams',
    'announcement.filterList',
    'announcement.savedQueries',
];

const CustomOnlineStore = () => {
    const token = React.useCallback(() => localStorage.getItem('token'), []);
    const enabled = React.useCallback(() => {
        const updateAspaProfile = localStorage.getItem('updateAspaProfile');
        return updateAspaProfile === null || updateAspaProfile === 'true';
    }, []);
    
    const [storeGetFromServer, setStoreGetFromServer] = useStore(
        'AspaProfile.fetched',
        '0'
    );

    const url = '/api/aspa_profile';

    // Call useStore for each key in a consistent order
    const storeHooks = STORE_KEYS.map(key => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [value, setValue] = useStore(key, '');
        return { key, value, setValue };
    });

    // Create memoized objects for values and setters
    const storeValues = React.useMemo(() => {
        const values: Record<string, string> = {};
        storeHooks.forEach(({ key, value }) => {
            values[key] = value;
        });
        return values;
    }, [storeHooks]);

    const storeSetters = React.useMemo(() => {
        const setters: Record<string, (value: string) => void> = {};
        storeHooks.forEach(({ key, setValue }) => {
            setters[key] = setValue;
        });
        return setters;
    }, [storeHooks]);

    // Create JSON object for API request
    const json = React.useMemo(() => {
        const result: Record<string, string> = {};
        STORE_KEYS.forEach(key => {
            result[key] = storeValues[key];
        });
        return result;
    }, [storeValues]);

    // Helper function to update store values from server data
    const updateStoreFromProfile = React.useCallback((profile: Record<string, string>) => {
        STORE_KEYS.forEach(key => {
            if (profile[key] && profile[key] !== '' && storeSetters[key]) {
                storeSetters[key](profile[key]);
            }
        });
    }, [storeSetters]);

    // Request options
    const createRequestOptions = React.useCallback((method: 'GET' | 'PUT') => ({
        method,
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: 'Bearer ' + token(),
        },
        ...(method === 'PUT' && { body: JSON.stringify(json) }),
    }), [token, json]);

    // Determine if we should fetch from server
    const fetchFromServer = React.useMemo(() => {
        const lastFetch = parseInt(storeGetFromServer);
        const twoMinutesAgo = new Date(Date.now()).setMinutes(-2);
        return lastFetch <= twoMinutesAgo;
    }, [storeGetFromServer]);

    // Track changes using a more reliable approach
    const [lastSentJson, setLastSentJson] = React.useState<string>('');
    const currentJsonString = JSON.stringify(json);
    const hasStoreValuesChanged = lastSentJson !== currentJsonString && currentJsonString !== '{}';

    const { data } = useQuery({
        queryKey: ['store', currentJsonString], // Include currentJsonString in queryKey to trigger on changes
        queryFn: async () => {
            const isGetRequest = fetchFromServer;
            const response = await fetch(
                url,
                createRequestOptions(isGetRequest ? 'GET' : 'PUT')
            );
            const data = await response.json();
            
            if (isGetRequest && data.profile) {
                updateStoreFromProfile(data.profile);
            }
            
            // Update tracking for PUT operations
            if (!isGetRequest) {
                setLastSentJson(currentJsonString);
            }
            
            // Update the fetch timestamp
            const now = new Date(Date.now()).setMinutes(0).toString();
            setStoreGetFromServer(parseInt(now).toString());
            
            return data;
        },
        enabled: !!token() && enabled() && (fetchFromServer || hasStoreValuesChanged),
        refetchInterval: fetchFromServer ? 30000 : false, // Only refetch for GET operations
    });

    if (!data) {
        return null;
    }
    //console.log(json);
    return (
        <>
            {/* <CustomOnlineStoreItem storeKey="student.filterList" />
            <CustomOnlineStoreItem storeKey="preferences.student.datagrid.columns" /> */}
        </>
    );
};

export default CustomOnlineStore;
//CustomOnlineStoreItem
